
import React, { useEffect, useRef, useState } from 'react'
import {
  AppBar, Badge, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, Grid,
  IconButton, LinearProgress, Stack, Tab, Tabs, Tooltip, Typography
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import { ClassNameMap, withStyles } from '@mui/styles'
import CallIcon from '@mui/icons-material/Call'
import StarRateIcon from '@mui/icons-material/StarRate';
import stylesheet from '../patients/detail/detail-container.stylesheet';
import { toast } from 'react-toastify'
import { useLocation } from 'react-router-dom'
import { TaskService } from './tasks.service'
import moment from 'moment'
import EmptyCardComponent from '../patients/detail/common/empty-card.component'
import Helper from '../../utils/helper';
import { useUpdateDataAction } from '../../utils/update-details-hook';
import { useSelector } from 'react-redux';
import { RootState, useAppThunkDispatch } from '../../redux/store';
import { updatePendingTaskCount } from '../../redux/patient-detail-slice';
import { setSelectedTask } from '../../redux/common-data-slice';
import AddTaskComponent from '../patients/detail/info-header/add-task.component';
import TaskActionComponent from '../patients/detail/info-header/task-action.component';
import TaskHistoryComponent from './task-history.component';
import PatientMessagesComponent, { useAwaitingMessageCount } from './patient-messages.component';
import TaskDelayComponent from '../patients/detail/info-header/task-delay.component'
import { stripPrefixAnywhere } from '../../utils/display-name-formatter'
import { profiler } from '../../utils/profiler'
import { agentEvalService } from '../../services/agent-eval.service'
import { FlowTask, AdhocFlowStartedEvent, AdhocFlowCompletedEvent } from '../../services/agent-eval.service'
import { setAdhocFlowCompleted } from '../../redux/agent-eval-slice'

interface IProps {
  input: any,
  updateTaskPanel?: any,
  classes: ClassNameMap,
  initialTaskData?: { tasks: any[]; users: any[] } | null
}

interface TabPanelProps {
  children?: React.ReactNode
  index: any
  value: any
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  )
}
function a11yProps(index: any) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  }
}

const PatientPrgTasksComponent: React.FC<IProps> = ({ input, updateTaskPanel, classes, initialTaskData }) => {
  // Seed taskLoading=true when parent hasn't prefetched yet — the panel's
  // LinearProgress shows while the rest of the page paints.
  const [taskLoading, setTaskLoading] = useState(!initialTaskData) as any
  const [taskData, setTaskData] = useState(initialTaskData?.tasks || []) as any
  const [usersList, setUsersList] = useState(initialTaskData?.users || []) as any
  const [currTask, setCurrTask] = useState({}) as any
  const [isTaskUpdate, setIsTaskUpdate] = useState(false)
  // Skip the filter-change effect's first firing — React runs deps-based effects
  // on mount too, which would duplicate the initial fetch.
  const isFirstMount = useRef(true)
  // Deep-link support for the cross-patient "messages awaiting me" dashboard card
  // (PORTAL_MESSAGING_ADR D13): it links here with ?tab=messages so the physician
  // lands on the Messages tab (index 2), not Tasks. Any other value → Tasks (0).
  const _deepLink = useLocation()
  const [value, setValue] = useState(
    new URLSearchParams(_deepLink.search).get('tab') === 'messages' ? 2 : 0,
  )
  const { updatePatientData } = useUpdateDataAction(input.patient_id)
  // Messages tab badge — how many threads are awaiting a reply. Lives here
  // because TabPanel unmounts inactive children, so the count cannot come from
  // the panel. Never throws; a failed badge is silent.
  const awaitingMessageCount = useAwaitingMessageCount(input.patient_id)
  const [excludeOptionalChecked, setExcludeOptionalChecked] = useState(input.patient_id ? true : false) as any;
  const [dueTodayChecked, setDueTodayChecked] = useState(false) as any;
  const [isCompleted, setIsCompleted] = useState(true) as any;
  const [myTasksChecked, setMyTasksChecked] = useState(false) as any;
  const patientDetail: any = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const multiProgramConfirm = useSelector((state: RootState) => state.multiProgramConfirm)
  const selectedTask = useSelector((state: RootState) => state.commonData.selectedTask)
  const loggedInUser = useSelector((state: RootState) => state.commonData.loggedInUser)
  const patientDetailStatus: any = useSelector((state: RootState) => state.patientDetail.status)

  // Flow orchestration state from agent-eval Redux slice
  const flowStatus = useSelector((state: RootState) => state.agentEval?.flowStatus)
  const currentFlowTask = useSelector((state: RootState) => state.agentEval?.currentFlowTask) as FlowTask | null
  const completedFlowTasks = useSelector((state: RootState) => state.agentEval?.completedFlowTasks) as FlowTask[] | undefined
  const flowTaskQueue = useSelector((state: RootState) => state.agentEval?.flowTaskQueue) as FlowTask[] | undefined
  const sessionId = useSelector((state: RootState) => state.agentEval?.sessionId) as string | null
  const currentAdhocFlow = useSelector((state: RootState) => state.agentEval?.currentAdhocFlow) as AdhocFlowStartedEvent | null
  const adhocFlowTasks = useSelector((state: RootState) => state.agentEval?.adhocFlowTasks) as AdhocFlowCompletedEvent[] | undefined
  const [advancingFlow, setAdvancingFlow] = useState(false)
  const [stoppingFlow, setStoppingFlow] = useState(false)
  const [stoppingAdhoc, setStoppingAdhoc] = useState(false)
  const [completingAdhoc, setCompletingAdhoc] = useState(false)

  const taskService = new TaskService();
  const dispatch = useAppThunkDispatch()

  /**
   * Get the live flow status for a task card.
   * Returns: 'completed' | 'running' | 'ready' | null
   * null means the task has no flow_name or session isn't active.
   * Any queued flow task shows as 'ready' (startable) — users pick
   * whichever flow they want to run, in any order.
   */
  const getFlowStatusForTask = (task: any): string | null => {
    if (!task.flow_name || !sessionId) return null
    if (completedFlowTasks?.some(ft => ft.task_id === task.id)) return 'completed'
    if (currentFlowTask?.task_id === task.id) return 'running'
    if (flowTaskQueue?.some(ft => ft.task_id === task.id)) return 'ready'
    return null
  }

  const handleAdvanceFlow = async (e: React.MouseEvent, flowName?: string) => {
    e.stopPropagation()
    e.preventDefault()
    if (!sessionId || advancingFlow) return

    setAdvancingFlow(true)
    try {
      await agentEvalService.advanceFlow(sessionId, flowName)
    } catch (err: any) {
      toast.error(err.message || 'Failed to start flow', Helper.bottom_center())
    } finally {
      setAdvancingFlow(false)
    }
  }

  const handleStopFlow = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!sessionId || stoppingFlow) return

    setStoppingFlow(true)
    try {
      await agentEvalService.stopFlow(sessionId)
      toast.info('Flow stopped', Helper.bottom_center())
    } catch (err: any) {
      toast.error(err.message || 'Failed to stop flow', Helper.bottom_center())
    } finally {
      setStoppingFlow(false)
    }
  }

  const handleStopAdhocFlow = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!sessionId || stoppingAdhoc) return

    setStoppingAdhoc(true)
    try {
      await agentEvalService.stopFlow(sessionId)
      toast.info('Flow stopped', Helper.bottom_center())
    } catch (err: any) {
      toast.error(err.message || 'Failed to stop flow', Helper.bottom_center())
    } finally {
      setStoppingAdhoc(false)
    }
  }

  const handleCompleteAdhocFlow = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!sessionId || !currentAdhocFlow || completingAdhoc) return

    const adhocSnapshot = { ...currentAdhocFlow }
    setCompletingAdhoc(true)
    try {
      await agentEvalService.completeTask(sessionId, currentAdhocFlow.task_id)
      // Optimistically update Redux — the gateway clears server state but
      // the task_completed SSE event isn't wired to a reducer, so dispatch directly.
      dispatch(setAdhocFlowCompleted({
        flow_name: adhocSnapshot.flow_name,
        task_id: adhocSnapshot.task_id,
        adhoc_origin: adhocSnapshot.adhoc_origin,
        started_at: '',
        completed_at: new Date().toISOString(),
        proposal_count: 0,
      }))
      toast.success('Flow completed', Helper.bottom_center())
    } catch (err: any) {
      toast.error(err.message || 'Failed to complete flow', Helper.bottom_center())
    } finally {
      setCompletingAdhoc(false)
    }
  }

  const taskTypeObj = {
    'To Do': 'To Do',
    'Attempted': 'Attempted',
    'delayed': 'Delayed',
    'Intentionally Delayed': 'Delayed',
    'Upcoming': 'Scheduled',
    'Completed': 'Completed',
    'Canceled': 'Cancelled',
  } as any

  // Hydrate from parent's prefetch when it resolves (null → populated).
  // Subsequent refreshes after save go through getTasks() in this component,
  // not through the prop, so this effect fires at most once per mount.
  useEffect(() => {
    if (initialTaskData) {
      setTaskData(initialTaskData.tasks)
      setUsersList(initialTaskData.users)
      setTaskLoading(false)
    }
  }, [initialTaskData])

  // Refetch on post-save refresh signal (selectedTask.isUpdate=true) or test-task dialog.
  // Initial mount does not fetch — detail-container prefetches and passes it via
  // initialTaskData, and setSelectedTask is dispatched with isUpdate:false on initial load.
  useEffect(() => {
    if (input?.isTestTask || (value === 0 && !taskLoading && input.isPrgTask &&
      selectedTask.prg && selectedTask.isUpdate
    )) {
      getTasks()
      if (selectedTask.isUpdate)
        dispatch(setSelectedTask({ ...selectedTask, isUpdate: false }))
    }
    if (patientDetailStatus === 'loading' && value === 1)
      setIsTaskUpdate(true)
  }, [value, patientDetailStatus, selectedTask, input?.isTestTask])

  // Refetch when a filter checkbox changes. The ref skips the unavoidable first firing
  // on mount (React runs deps-based effects on first render).
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false
      return
    }
    if (input?.isTestTask || (value === 0 && !taskLoading && input.isPrgTask && selectedTask.prg)) {
      getTasks()
    }
  }, [myTasksChecked, dueTodayChecked, isCompleted, excludeOptionalChecked])

  const resetTaskUpdate = () => setIsTaskUpdate(false)

  const handleExcludeOptionalChange = (e: any) => {
    e.stopPropagation()
    e.preventDefault()
    localStorage.setItem('excludeOptionalChecked', e.target.checked);
    setExcludeOptionalChecked(e.target.checked);
  }

  const handleTaskCompletedChange = (e: any) => setIsCompleted(e.target.checked);
  const handleMyTasksChange = (e: any) => setMyTasksChecked(e.target.checked);
  const handleDueTodayChange = (e: any) => setDueTodayChecked(e.target.checked);

  const getTasks = () => {
    setTaskLoading(true)
    profiler.start('getTasks API call')

    taskService.getPatientTasks(
      (!myTasksChecked) ? '*' : loggedInUser.sub,
      input.patient_id,
      input.isTestTask ? input.ref_id : '',
      '',
      '',
      (selectedTask.prg),
      '',//	stageName,
      excludeOptionalChecked,
      dueTodayChecked,
      isCompleted,
      false,
      '',
      '',
      '',
      500,
      1,
      false,
      false)
      .then((res: any) => {
        profiler.end('getTasks API call')
        setTaskLoading(false)
        setTaskData(res?.data?.tasks || [])
        setUsersList(res?.data?.picklist_values?.users || [])
      }).catch((err: any) => {
        profiler.end('getTasks API call')
        setTaskLoading(false)
        toast.error(Helper.getError(err), Helper.bottom_center())
      })
  }

  const handleCardClick = (e: any, task: any) => {
    e.stopPropagation();
    e.preventDefault();

    setCurrTask({
      id: task.id,
      owner_uuid: task?.owner?.sso_id || '',
      title: task.title,
      ref_id: task.ref_id,
      task_status: task.task_status,
      action_type: task.action_type,
      due_date: task?.due_date || null,
      note: task?.notes[0]?.text || '',
      owner_role: task.owner_role,
      is_adhoc: task.is_adhoc,
      attempts: task.attempts,
      delays: task.delays,
      optional: task.optional,
      action: 'edit',
      assignment: {
        assigned_to: task?.assignee?.name || '',
        assignment_date: task?.assignment_date
      },
    })
  }

  const getTaskStatus = (currTask: any, isDelay: boolean) => {
    let status = taskTypeObj[(currTask['task_status'])] || currTask['task_status']
    if (status === 'To Do')
      return ''

    return <Grid item sx={{ minWidth: '10%', width: 'fit-content !important', paddingTop: '0rem !important' }}>
      <Typography variant="body2" pr={0.5} className={classes.desc} align="right">
        {status.length > 9 ? <Tooltip title={status}>
          <Typography component={'span'} className={classes.desc} variant="body2">
            {Helper.toCamelCase(status.length > 9 ? status.slice(0, 8) + "..." : status)}
          </Typography>
        </Tooltip>
          : <Typography component={'span'} className={`${classes.desc}`} variant="body2">
            {Helper.toCamelCase(status)}
          </Typography>}
      </Typography>
    </Grid>
  }

  const getTaskNote = (currTask: any) => {
    const delay = currTask?.delays?.last_delayed_on || '' as any
    const attempt = currTask?.attempts?.last_attempt_date || '' as any
    const latestNote = currTask?.notes[0]?.created_on || ''
    const defaultNote = latestNote ? currTask?.notes[0]?.text : ''
    const latestArray = [
      {
        date: delay,
        note: (delay && ['delayed', 'Intentionally Delayed'].includes(currTask.task_status))
          ? currTask?.delays?.latest_delay['justification']
          : defaultNote
      },
      { date: attempt, note: attempt ? currTask?.attempts?.data[0]?.note : '' },
      { date: latestNote, note: defaultNote }
    ]
    const latestNoteObj = [...latestArray].sort((a: any, b: any) => a.date < b.date ? 1 : -1);
    let status = latestNoteObj?.[0]?.note || ''

    return status.length > 50 ?
      <Tooltip title={status}>
        <Typography className={classes.desc} variant="body2" pt={0.1}>
          {`${status.slice(0, 47)} ...`}
        </Typography>
      </Tooltip>
      : <Typography className={classes.desc} variant="body2" pt={0.1}>
        {status}
      </Typography>
  }

  const getEnrollmentId = () => patientDetail && selectedTask.prg &&
    patientDetail?.program_enrollments.filter((m: { program: any }) =>
      m.program === selectedTask.prg)?.[0]?.enrollment_id || null

  const updateTaskCount = () => {
    const enrollment_id = getEnrollmentId();
    const currProgram = selectedTask.prg

    enrollment_id && currProgram && taskService.getTaskPendingCount(enrollment_id).then((res: any) => {
      if (res.data) {
        let taskCount = { ...patientDetail.pending_task_counts }
        const pendingCounts = multiProgramConfirm?.find((m) => m.program === currProgram)?.pending || 0
        const prgObj = patientDetail.program_enrollments?.find((f: { program: any }) => f.program === currProgram) || {} as any

        if ((res.data.count === 0 && prgObj?.current_stage === 'enrollment') ||
          (res.data.count === 0 && pendingCounts === 0)
        ) {
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        } else {
          taskCount[currProgram] = res.data?.count
          dispatch(updatePendingTaskCount(taskCount))
        }
      }
    }).catch((err: any) => toast.error(Helper.getError(err), Helper.bottom_center()))
  }

  const handleCloseTaskAction = () => {
    setCurrTask({ id: '', action: '' })
  }

  const [quickCompleteLoading, setQuickCompleteLoading] = useState<string | null>(null)

  const handleQuickComplete = async (e: React.MouseEvent, task: any) => {
    e.stopPropagation()
    e.preventDefault()

    if (task.task_status === 'Completed') return

    setQuickCompleteLoading(task.id)
    try {
      await taskService.putTaskSchedule(task.id, {
        task_status: 'COMPLETED',
        is_updated_today: true
      })
      // Update local state immediately to avoid flicker
      if (isCompleted) {
        // If "Hide completed" is checked, remove the task from the list
        setTaskData((prev: any[]) => prev.filter((t: any) => t.id !== task.id))
      } else {
        // Otherwise update its status in place
        setTaskData((prev: any[]) => prev.map((t: any) =>
          t.id === task.id ? { ...t, task_status: 'Completed', completion_date: new Date().toISOString() } : t
        ))
      }
      if (!input?.isTestTask) {
        updateTaskCount()
      } else {
        updateTaskPanel()
      }
      getTasks()
    } catch (err: any) {
      toast.error(Helper.getError(err), Helper.bottom_center())
    } finally {
      setQuickCompleteLoading(null)
    }
  }

  // Test/demo patients only. The button is hidden for real patients; the
  // server (settings.TEST_HOSPITAL_NAMES) is the actual gate.
  const TEST_HOSPITAL_NAMES = ['atman_test', 'atman_clinic']
  const isTestPatient = TEST_HOSPITAL_NAMES.includes(
    (patientDetail?.hospital_name || '').toLowerCase()
  )
  const [completeAllLoading, setCompleteAllLoading] = useState(false)

  const handleCompleteAllTasks = async () => {
    if (!input.patient_id) return
    if (!window.confirm('Complete all open tasks EXCEPT the last one in the order for this test patient?')) return

    setCompleteAllLoading(true)
    try {
      const res: any = await taskService.completeAllPatientTasks(input.patient_id)
      toast.success(res?.data?.message || 'All tasks completed', Helper.bottom_center())
      if (!input?.isTestTask) {
        updateTaskCount()
      } else {
        updateTaskPanel()
      }
      getTasks()
    } catch (err: any) {
      toast.error(Helper.getError(err), Helper.bottom_center())
    } finally {
      setCompleteAllLoading(false)
    }
  }

  const updateTaskData = (currTaskObj: any) => {
    if (currTaskObj.action === 'delete') {
      setCurrTask({ ...currTask, action: 'delete' })
    }
    else if (currTaskObj.action === 'delete_cancel') {
      setCurrTask({ ...currTask, action: 'edit' })
    } else {
      handleCloseTaskAction()

      if (!input?.isTestTask) {
        updateTaskCount()
      } else {
        updateTaskPanel()
      }
      getTasks()
    }
  }

  const renderAttemptIcon = () => {
    return <IconButton
      aria-label='Attempts details'
      size='small'
      sx={{ padding: '5px 0px 0px 2px' }}
    >
      <CallIcon className={classes.patientTaskIcon}
        sx={{
          fontSize: '0.65rem !important',
          transform: 'rotate(16deg) !important',
          fill: '#4ccd4c'
        }} />
    </IconButton>
  }

  const getTaskCard = (row: any) => {
    const liveFlowStatus = getFlowStatusForTask(row)

    // Determine background color — flow status takes priority when session is active
    const getCardBgColor = () => {
      if (currTask.id === row.id && currTask.action === 'delete') return '#ee18041c'
      if (liveFlowStatus === 'running') return '#1976d214'  // Blue tint for active flow
      if (liveFlowStatus === 'completed') return '#4caf5014'  // Green tint for completed flow
      if (liveFlowStatus === 'ready') return '#ff980014'  // Orange tint for next-up
      // Default logic
      if (row.last_modified_date &&
        (row.task_status !== 'Completed' && moment.unix(row.last_modified_date).format('MMM DD, YYYY') === moment().format('MMM DD, YYYY'))
      ) return '#6bd21914'
      if (!row?.stage?.display_name || row.task_status === 'Completed') return '#8a8a8a47'
      if (row.task_status === 'To Do') return 'var(--color-attention-bg)'
      return 'var(--color-paper)'
    }

    return row && <Box mb={1} className={classes.customMiniCard}>
      <Card variant="outlined" className={classes.compactCard}
        onClick={(e: any) => handleCardClick(e, row)}
        sx={{
          boxShadow: currTask.id === row.id ? '0px 1px 1px 1px var(--color-border-light) !important' : '',
          border: liveFlowStatus === 'running' ? '1px solid #1976d280' : undefined,
          opacity: 1,
        }}
      >
        <CardContent className={classes.cardsContentPadding}
          sx={{ backgroundColor: getCardBgColor() }}
        >
          <Grid container direction='column' className={classes.compactCardContent} alignItems="center" spacing={0}>
            <Grid item className={classes.fullWidth}>
              <Grid container spacing={0} direction="row" justifyContent="space-between" alignItems={'center'}>
                <Grid item xs>
                  <Stack direction="row" alignItems="center" spacing={0}>
                    {/* Quick complete checkmark — always available for non-completed tasks */}
                    {row.task_status !== 'Completed' && liveFlowStatus !== 'completed' && (
                      <Tooltip title="Mark complete">
                        {quickCompleteLoading === row.id ? (
                          <CircularProgress size={14} sx={{ color: 'var(--color-primary)' }} />
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => handleQuickComplete(e, row)}
                            sx={{ padding: '1px' }}
                          >
                            <CheckCircleOutlineIcon sx={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }} />
                          </IconButton>
                        )}
                      </Tooltip>
                    )}
                    {/* Play button — start/restart a flow task that's ready */}
                    {liveFlowStatus === 'ready' && (
                      <Tooltip title="Start this flow">
                        {advancingFlow ? (
                          <CircularProgress size={14} sx={{ color: '#ff9800' }} />
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => handleAdvanceFlow(e, row.flow_name)}
                            sx={{ padding: '1px' }}
                          >
                            <PlayArrowIcon sx={{ fontSize: '0.9rem', color: '#ff9800' }} />
                          </IconButton>
                        )}
                      </Tooltip>
                    )}
                    {/* Stop button — cancel the running flow, return to ready */}
                    {liveFlowStatus === 'running' && (
                      <Tooltip title="Stop this flow">
                        {stoppingFlow ? (
                          <CircularProgress size={14} sx={{ color: '#d32f2f' }} />
                        ) : (
                          <IconButton
                            size="small"
                            onClick={handleStopFlow}
                            sx={{ padding: '1px' }}
                          >
                            <StopIcon sx={{ fontSize: '0.9rem', color: '#d32f2f' }} />
                          </IconButton>
                        )}
                      </Tooltip>
                    )}
                    <Typography variant="body2" className={classes.desc} sx={{ paddingTop: '0.1rem', wordBreak: 'break-all', alignItems: 'center' }}>
                      {stripPrefixAnywhere(row.title) || row.title}
                      {!row.optional &&
                        <StarRateIcon sx={{ fontSize: '0.4rem !important', fill: 'red', paddingBottom: '0rem' }} />
                      }
                    </Typography>
                    {row.owner_role && (
                      <Chip size="small"
                        label={row.owner_role}
                        sx={{
                          height: 14, fontSize: '0.5rem', ml: 0.5,
                          backgroundColor: row.owner_role === 'Provider' ? 'var(--color-role-provider-bg)' : 'var(--color-role-navigator-bg)',
                          color: row.owner_role === 'Provider' ? 'var(--color-role-provider-text)' : 'var(--color-role-navigator-text)',
                        }}
                      />
                    )}
                    {row.is_adhoc && (
                      <Chip size="small"
                        label={row.adhoc_origin === 'Cross Stage' ? `Cross-Stage${row.stage?.display_name ? ` (${row.stage.display_name})` : ''}` :
                               row.adhoc_origin === 'Patient Triggered' ? 'Patient Triggered' :
                               row.adhoc_origin === 'Provider Initiated' ? 'Provider Initiated' : 'Ad-Hoc'}
                        sx={{
                          height: 14, fontSize: '0.5rem', ml: 0.5,
                          backgroundColor: row.adhoc_origin === 'Cross Stage' ? 'rgba(33, 150, 243, 0.12)' : 'rgba(255, 152, 0, 0.12)',
                          color: row.adhoc_origin === 'Cross Stage' ? '#1565c0' : '#e65100',
                        }}
                      />
                    )}
                  </Stack>
                </Grid>
                {/* Flow status badge or regular task status */}
                {liveFlowStatus === 'running' ? (
                  <Grid item sx={{ minWidth: '10%', width: 'fit-content !important', paddingTop: '0rem !important' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#1976d2', fontWeight: 500 }}>
                      In Progress
                    </Typography>
                  </Grid>
                ) : liveFlowStatus === 'ready' ? (
                  <Grid item sx={{ minWidth: '10%', width: 'fit-content !important', paddingTop: '0rem !important' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#ff9800', fontWeight: 500 }}>
                      Ready
                    </Typography>
                  </Grid>
                ) : getTaskStatus(row, row?.delays?.count > 0)}
              </Grid>
            </Grid>

            <Grid item className={classes.fullWidth} sx={{ minHeight: '0rem !important' }}>
              <Grid container spacing={0.5} direction="row" justifyContent={'space-between'} alignItems={'center'}>
                <Grid item xs alignItems={'flex-end'}>
                  <Stack direction={'row'} justifyItems={'baseline'} alignItems={'center'}>
                    <Typography variant="body2" className={classes.desc}>
                      {getTaskNote(row)}
                    </Typography>
                    {row.action_type === 'Call' && row?.attempts?.count > 0 &&
                      <Tooltip
                        title={<Card className={classes.taskDetailsTooltip}>
                          <TaskDelayComponent
                            input={{
                              attempts: row.attempts
                            }}
                          />
                        </Card>
                        }
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: 'var(--color-background-subtle) !important',
                              maxWidth: '35rem !important',
                              padding: '0px !important'
                            }
                          }
                        }}
                        placement="bottom"
                      >
                        <Badge badgeContent={row?.attempts?.count} color="primary"
                          className={classes.taskAttemptBadge}
                          sx={{
                            paddingBottom: '0.25rem',
                            '& .MuiBadge-badge': {
                              top: '5px !important',
                              right: '-2px !important',
                              fontSize: '0.5rem !important'
                            }
                          }}>
                          {renderAttemptIcon()}
                        </Badge>
                      </Tooltip>
                    }
                  </Stack>
                </Grid>
                <Grid item sx={{ minWidth: '10%', width: 'fit-content !important' }}>
                  {row.completion_date
                    ? <Typography textAlign={'right'} mr={0.5}
                      className={classes.cardDate}
                      sx={{ paddingRight: '0rem !important', paddingBottom: '0.15rem !important' }}
                      variant="subtitle2"
                      color="textSecondary"
                    >
                      <small>{moment(row.completion_date).format("MM/DD/YY")}</small>
                    </Typography>
                    : row?.due_date
                      ? <Typography textAlign={'right'} mr={0.5}
                        className={classes.cardDate}
                        sx={{ paddingRight: '0rem !important', paddingBottom: '0.15rem !important' }}
                        variant="subtitle2"
                        color="textSecondary"
                      >
                        <small>{moment(row.due_date).format("MM/DD/YY")}</small>
                      </Typography>
                      : row?.assignment_date &&
                      <Typography textAlign={'right'} mr={0.5}
                        className={classes.cardDate}
                        sx={{ paddingRight: '0rem !important', paddingBottom: '0.15rem !important' }}
                        variant="subtitle2"
                        color="textSecondary"
                      >
                        <small>{moment(row.assignment_date).format("MM/DD/YY")}</small>
                      </Typography>
                  }
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box >
  }

  const getPrgDisplayName = () => selectedTask.prg && patientDetail?.program_enrollments
    ?.find((m: { program: any }) => m.program === selectedTask.prg)
    ?.program_display_name

  const updateAddTaskData = () => (value === 0) ? getTasks() : setIsTaskUpdate(true)

  const handleAddTask = (action: any) => {
    const payload = (action === 'log_activity')
      ? {
        patient_id: input.patient_id,
        assignee_uuid: loggedInUser.sub,
        date: Helper.getUTCDate(''),
        program_name: selectedTask?.prg,
        action: action,
      }
      : {
        patient_id: input.patient_id,
        owner_uuid: '',// loggedInUser.sub,
        action: action,
        program_name: selectedTask?.prg,
      }
    setCurrTask(payload)
  }

  const renderAddTasks = () => {
    return <Box pb={0.3}>
      {['Add', 'log_activity'].includes(currTask.action) && <Divider />}
      <Stack direction={'row'} spacing={1.5} sx={{ paddingbottom: `0.5rem`, paddingTop: currTask.action ? '0.8rem' : '' }}>
        <Button variant="outlined"
          className={`${classes.taskAddBtn} ${currTask.action === 'Add' ? classes.medSubSelectActiveBtn : ''}`}
          onClick={() => handleAddTask('Add')}
        >
          <Typography className={classes.desc} variant="body2"> {'Add task'} </Typography>
        </Button>
        <Button variant="outlined"
          className={`${classes.taskAddBtn} ${currTask.action === 'log_activity' ? classes.medSubSelectActiveBtn : ''}`}
          onClick={() => handleAddTask('log_activity')}
        >
          <Typography className={classes.desc} variant="body2"> {'Log activity'}</Typography>
        </Button>
      </Stack>
      {currTask.action === 'Add' &&
        <Box sx={{ padding: '1rem 0 0 0rem', }}>
          <AddTaskComponent
            input={{
              currTask: currTask,
              isTaskPanel: true,
              users: usersList,
            }}
            updateTaskData={updateAddTaskData}
            cancelTask={handleCloseTaskAction}
          />
        </Box>
      }
      {currTask.action === 'log_activity' &&
        <Box sx={{ padding: '1rem 0 0 0rem' }}>
          <AddTaskComponent
            input={{
              currTask: currTask,
              isTaskPanel: true,
              users: usersList
            }}
            updateTaskData={updateAddTaskData}
            cancelTask={handleCloseTaskAction}
          />
        </Box>
      }
      {['Add', 'log_activity'].includes(currTask.action) && <Divider />}
    </Box>
  }

  const renderFilters = () => {
    return <Stack
      direction={'column'}
      justifyContent={'space-between'}
      sx={{
        padding: input?.isTestTask ? '0rem 0rem 0.25rem 0rem' : '0.3rem 0rem 0.3rem 0.2rem'
      }}
    >
      {!input?.isTestTask &&
        <Stack direction={'row'} spacing={1} justifyContent="flex-start" alignItems="bottom">
          <FormControlLabel
            control={<Checkbox
              size="small"
              sx={{ padding: '0rem !important', transform: "scale(0.6)", }}
              checked={excludeOptionalChecked}
              onChange={handleExcludeOptionalChange}
            />}
            label={<Typography className={classes.desc} variant="body2">
              {"Show required"}
            </Typography>
            }
            aria-setsize={10}
          />
          <FormControlLabel
            control={<Checkbox
              size="small"
              sx={{ padding: '0rem !important', transform: "scale(0.6)", }}
              checked={myTasksChecked}
              onChange={handleMyTasksChange}
            />}
            label={<Typography className={classes.desc} variant="body2">
              {`My tasks`}
            </Typography>
            }
            aria-setsize={10}
          />
        </Stack>
      }
      <Stack direction={'row'} spacing={1} justifyContent="flex-start" alignItems="bottom">
        {!input?.isTestTask &&
          <FormControlLabel
            control={<Checkbox
              size="small"
              sx={{ padding: '0rem !important', transform: "scale(0.6)", }}
              checked={dueTodayChecked}
              onChange={handleDueTodayChange}
            />}
            label={<Typography className={classes.desc} variant="body2">
              {"Due today"}
            </Typography>
            }
            aria-setsize={10}
          />
        }
        <FormControlLabel
          control={<Checkbox
            size="small"
            sx={{ padding: '0rem !important', transform: "scale(0.6)", }}
            checked={isCompleted}
            onChange={handleTaskCompletedChange}
          />}
          label={<Typography className={classes.desc} variant="body2">
            {`Hide completed`}
          </Typography>
          }
          aria-setsize={10}
        />
        {isTestPatient &&
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={completeAllLoading}
            onClick={handleCompleteAllTasks}
            sx={{ ml: 'auto', textTransform: 'none', py: 0, minHeight: '1.5rem' }}
          >
            {completeAllLoading ? 'Completing…' : 'Complete all but last'}
          </Button>
        }
      </Stack>
    </Stack>
  }
  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue)
    if (newValue === 1)
      setIsTaskUpdate(true)
  }

  /** Humanize flow_name: "update_pharmacy" → "Update Pharmacy" */
  const humanizeFlowName = (flowId: string): string => {
    return flowId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }

  const renderAdhocFlowSection = () => {
    if (!sessionId) return null
    const hasAdhoc = currentAdhocFlow || (adhocFlowTasks && adhocFlowTasks.length > 0)
    if (!hasAdhoc) return null

    return (
      <Box sx={{ mb: 1, mx: 0.2 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#e65100', fontSize: '0.65rem' }}>
          Ad-Hoc Flows
        </Typography>
        {/* Currently running adhoc flow */}
        {currentAdhocFlow && (
          <Box sx={{ mb: 0.5 }}>
            <Card variant="outlined" sx={{
              border: currentAdhocFlow.adhoc_origin === 'cross_stage'
                ? '1px solid rgba(33, 150, 243, 0.4)' : '1px solid rgba(255, 152, 0, 0.4)',
              backgroundColor: currentAdhocFlow.adhoc_origin === 'cross_stage'
                ? 'rgba(33, 150, 243, 0.06)' : 'rgba(255, 152, 0, 0.06)',
            }}>
              <CardContent sx={{ padding: '4px 8px !important', '&:last-child': { paddingBottom: '4px !important' } }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {/* Manual complete checkmark */}
                  <Tooltip title="Mark complete">
                    {completingAdhoc ? (
                      <CircularProgress size={12} sx={{ color: '#4caf50' }} />
                    ) : (
                      <IconButton size="small" onClick={handleCompleteAdhocFlow} sx={{ padding: '1px' }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }} />
                      </IconButton>
                    )}
                  </Tooltip>
                  {/* Stop flow button */}
                  <Tooltip title="Stop this flow">
                    {stoppingAdhoc ? (
                      <CircularProgress size={12} sx={{ color: '#d32f2f' }} />
                    ) : (
                      <IconButton size="small" onClick={handleStopAdhocFlow} sx={{ padding: '1px' }}>
                        <StopIcon sx={{ fontSize: '0.8rem', color: '#d32f2f' }} />
                      </IconButton>
                    )}
                  </Tooltip>
                  {/* Running spinner */}
                  <CircularProgress size={12} sx={{ color: currentAdhocFlow.adhoc_origin === 'cross_stage' ? '#2196f3' : '#ff9800' }} />
                  <Typography variant="body2" className={classes.desc} sx={{ fontWeight: 500 }}>
                    {humanizeFlowName(currentAdhocFlow.flow_name)}
                  </Typography>
                  <Chip
                    size="small"
                    label={currentAdhocFlow.adhoc_origin === 'patient_triggered' ? 'Patient' :
                           currentAdhocFlow.adhoc_origin === 'provider_initiated' ? 'Provider' :
                           currentAdhocFlow.adhoc_origin === 'cross_stage' ? 'Cross-Stage' : 'Ad-Hoc'}
                    sx={{
                      height: 16, fontSize: '0.55rem', fontWeight: 600,
                      backgroundColor: currentAdhocFlow.adhoc_origin === 'cross_stage'
                        ? 'rgba(33, 150, 243, 0.15)' : 'rgba(255, 152, 0, 0.15)',
                      color: currentAdhocFlow.adhoc_origin === 'cross_stage' ? '#1565c0' : '#e65100',
                    }}
                  />
                  {currentAdhocFlow.adhoc_origin === 'cross_stage' && currentAdhocFlow.task_stage && (
                    <Typography variant="caption" sx={{ color: '#888', fontSize: '0.55rem' }}>
                      from {currentAdhocFlow.task_stage}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}
        {/* Completed adhoc flows */}
        {adhocFlowTasks && adhocFlowTasks.map((adhoc, idx) => (
          <Box key={`adhoc-done-${idx}`} sx={{ mb: 0.5 }}>
            <Card variant="outlined" sx={{
              border: '1px solid rgba(76, 175, 80, 0.3)',
              backgroundColor: 'rgba(76, 175, 80, 0.04)',
              opacity: 0.85,
            }}>
              <CardContent sx={{ padding: '4px 8px !important', '&:last-child': { paddingBottom: '4px !important' } }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <CheckCircleOutlineIcon sx={{ fontSize: '0.8rem', color: '#4caf50' }} />
                  <Typography variant="body2" className={classes.desc}>
                    {humanizeFlowName(adhoc.flow_name)}
                  </Typography>
                  <Chip
                    size="small"
                    label={adhoc.adhoc_origin === 'patient_triggered' ? 'Patient' :
                           adhoc.adhoc_origin === 'provider_initiated' ? 'Provider' :
                           adhoc.adhoc_origin === 'cross_stage' ? 'Cross-Stage' : 'Ad-Hoc'}
                    sx={{
                      height: 16, fontSize: '0.55rem',
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      color: '#2e7d32',
                    }}
                  />
                  {adhoc.proposal_count > 0 && (
                    <Typography variant="caption" sx={{ color: '#888', fontSize: '0.55rem' }}>
                      {adhoc.proposal_count} proposal{adhoc.proposal_count !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        ))}
        <Divider sx={{ mt: 0.5 }} />
      </Box>
    )
  }

  const renderTaskList = () => {
    return <Box>
      {input?.isTestTask &&
        <Typography variant='h6' color="textSecondary" className={classes.subHeaderTitle}>
          {`Tasks`}
        </Typography>
      }
      {renderFilters()}
      {renderAdhocFlowSection()}

      <Box className={classes.fullWidth} sx={{ padding: '0.1rem 0.2rem 0rem 0rem' }}>
        {!taskLoading && taskData?.map((i: any) => <Box className={classes.fullWidth}>
          <Box>{getTaskCard(i)}</Box>

          {i.id === currTask.id && ['delete', 'edit'].includes(currTask.action) &&
            <TaskActionComponent
              input={{
                currTask: currTask,
                isPatientTask: Boolean(input.patient_id),
                isTaskPanel: true,
                isTestTask: input?.isTestTask,
                users: usersList,
              }}
              updateTaskData={updateTaskData}
              closeTaskAction={handleCloseTaskAction}
            />
          }
        </Box>
        )}
        {!taskLoading && Object.keys(taskData)?.length <= 0 &&
          <Box pt={2}><EmptyCardComponent /></Box>
        }
      </Box>
    </Box>
  }

  return <>
    {input?.isTestTask
      ? <Box>
        {taskData?.length > 0 && renderTaskList()}
      </Box>
      : <Box ml={0.5} mr={0.8} className={classes.topTHeader}>
        <Typography variant="h6" color="textSecondary" className={classes.headerTitle}>
          {`Tasks & Activity - ${getPrgDisplayName()}`}
        </Typography>
        <Box sx={{ height: 'inherit', overflowY: 'auto', overflowX: 'hidden' }}>
          <Box pt={0.5}>{renderAddTasks()}</Box>
          <AppBar position="sticky" color="inherit"
            sx={{
              backgroundColor: "var(--color-background-subtle)",
              boxShadow: '0 2px 4px -1px rgba(0,0,0,0.12) !important'
            }}
            classes={{ root: 'task-title' }}
          >
            <Tabs value={value} onChange={handleChange}
              sx={{
                '&.MuiTabs-root': {
                  minHeight: '0.5rem !important',
                  height: '1.5rem !important',
                  padding: '0px !important',
                  fontSize: '0.65rem !important',
                },
                '.MuiTab-root': {
                  minHeight: '0.5rem !important',
                  color: 'var(--color-text-primary) !important',
                },
                '.Mui-selected': {
                  color: 'var(--color-primary) !important',
                }
              }}>
              <Tab label="Tasks" {...a11yProps(0)} />
              <Tab label="Activity" {...a11yProps(1)} />
              {/* Badge rendered INLINE, not floating. The Tabs strip above is
                  pinned to height 1.5rem, so an absolutely-positioned badge is
                  clipped at the top edge (RD 2026-07-28: "the 1 is a bit cut
                  off"). position:static drops it into normal flow beside the
                  label, where the row height cannot crop it. */}
              <Tab {...a11yProps(2)} label={
                <Badge badgeContent={awaitingMessageCount} color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      position: 'static', transform: 'none', marginLeft: '5px',
                      fontSize: '0.55rem', height: 15, minWidth: 15, padding: '0 4px',
                    },
                  }}>
                  Messages
                </Badge>
              } />
            </Tabs>
          </AppBar>
          {taskLoading && <LinearProgress />}
          <Box>
            <Box className={classes.rationaleDialogHeader} />
            <TabPanel value={value} index={0}>
              {renderTaskList()}
            </TabPanel>
            <TabPanel value={value} index={1}>
              <TaskHistoryComponent
                input={{
                  patientId: input.patient_id,
                  isTaskPanel: true,
                  isTaskUpdate: isTaskUpdate
                }}
                updateIsTaskUpdate={resetTaskUpdate}
              />
            </TabPanel>
            <TabPanel value={value} index={2}>
              <PatientMessagesComponent patientRefId={input.patient_id} />
            </TabPanel>
          </Box>
        </Box>
      </Box >
    }
  </>
}

export default withStyles(stylesheet)(PatientPrgTasksComponent)